<template>
  <div class="page-container login-page">
    <div class="content-wrapper">
      <h1 class="title">魔法秘境-英语</h1>
      <van-form @submit="onSubmit" :show-error="false">
        <div class="section">
          <div class="invite-code-section">
            <div class="label">邀请码</div>
            <div class="code">{{ inviteCode }}</div>
          </div>

          <van-field
            v-model="phoneNumber"
            name="phoneNumber"
            label="手机号码"
            placeholder="请输入手机号码"
            :rules="phoneRules"
            :disabled="loading"
          />
          <van-field
            v-model="verificationCode"
            center
            clearable
            maxlength="4"
            type="tel"
            label="验证码"
            placeholder="验证码"
            :rules="[
              { required: true, message: '请填写验证码' },
              { pattern: /^\d{4}$/, message: '请输入4位数字验证码' }
            ]"
          >
            <template #button>
              <van-button
                size="mini"
                type="primary"
                @click="handleSendCode($event)"
                :disabled="cooldown > 0 || !phoneNumber || loading"
                :loading="sendingCode"
                style="min-width: 80px; padding: 0 8px;"
              >
                {{ cooldown > 0 ? `${cooldown}s` : '获取验证码' }}
              </van-button>
            </template>
          </van-field>
        </div>

        <div class="submit-btn">
          <van-button 
            round 
            block 
            type="primary" 
            native-type="submit"
            :loading="loading"
            :disabled="loading"
          >
            {{ loading ? '提交中...' : '下一步' }}
          </van-button>
        </div>
      </van-form>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { showToast, showDialog } from 'vant'
import { sendVerificationCode as sendCode, verifyCode } from '@/api/user'

export default {
  name: 'LoginView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const phoneNumber = ref('')
    const verificationCode = ref('')
    const inviteCode = ref('')
    const cooldown = ref(0)
    const loading = ref(false)
    const sendingCode = ref(false)

    // 表单验证规则
    const phoneRules = [
      { required: true, message: '请输入手机号码' },
      { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' }
    ]

    onMounted(() => {
      inviteCode.value = route.params.inviteCode || ''
      if (!inviteCode.value) {
        showDialog({
          title: '提示',
          message: '邀请码无效，请检查链接是否正确',
          confirmButtonText: '确定'
        }).then(() => {
          // 可以根据需求决定是否跳转到其他页面
        })
      }
    })

    const startCooldown = () => {
      cooldown.value = 60
      const timer = setInterval(() => {
        cooldown.value--
        if (cooldown.value <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    }

    const handleSendCode = async (event) => {
      event.preventDefault()
      if (!/^1[3-9]\d{9}$/.test(phoneNumber.value)) {
        showToast('请输入正确的手机号码')
        return
      }

      try {
        sendingCode.value = true
        const response = await sendCode(phoneNumber.value)
        if (response.Success) {
          startCooldown()
          showToast('验证码已发送')
        } else {
          showToast(response.Msg || '发送验证码失败，请稍后重试')
        }
      } catch (error) {
        showToast('发送验证码失败，请稍后重试')
        console.error('发送验证码失败:', error)
      } finally {
        sendingCode.value = false
      }
    }

    const onSubmit = async () => {
      if (loading.value) return

      if (!/^\d{4}$/.test(verificationCode.value)) {
        showToast('请输入4位数字验证码')
        return
      }

      try {
        loading.value = true
        const response = await verifyCode({
          phone: phoneNumber.value,
          code: verificationCode.value,
          inviteCode: inviteCode.value
        })

        if (response.Success) {
          // 存储 token，如果有 Bearer 前缀则去掉
          const token = response.Data.Jwt.replace(/^Bearer\s+/i, '')
          localStorage.setItem('token', token)
          
          // 存储用户信息
          const userInfo = {
            nickName: response.Data.NickName,
            hasPreTest: response.Data.HasPreTest,
            isBookingInProgress: response.Data.IsBookingInProgress,
            userType: response.Data.UserType,
            isVIP: response.Data.IsVIP
          }
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          
          showToast('登录成功')
          // 根据 HasPreTest 决定跳转路径
          if (response.Data.HasPreTest) {
            // 如果已经做过测试，直接跳转到下载页面
            router.push('/download')
          } else {
            // 如果没有做过测试，跳转到用户信息页面
            router.push('/user-info')
          }
        } else {
          showToast(response.Msg || '验证失败，请检查验证码是否正确')
        }
      } catch (error) {
        console.error('登录失败:', error)
        showToast('登录失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    return {
      phoneNumber,
      verificationCode,
      inviteCode,
      cooldown,
      loading,
      sendingCode,
      phoneRules,
      handleSendCode,
      onSubmit
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500&family=Ma+Shan+Zheng&display=swap');

.login-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  position: relative;
  padding: 20px;
  overflow: hidden;
}

.login-page::before,
.login-page::after {
  content: '';
  position: absolute;
  width: 300px;
  height: 300px;
  background-repeat: no-repeat;
  background-size: contain;
  pointer-events: none;
  z-index: 1;
}

.login-page::before {
  top: -50px;
  left: -50px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Cpath d='M280,150 C280,120 250,90 220,90 C190,90 170,120 190,150 C210,180 250,195 280,180 C310,165 320,135 300,105 C280,75 230,60 180,75 C130,90 90,135 110,180 C130,225 180,255 230,240 C280,225 310,180 290,135' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.3'/%3E%3Cpath d='M250,135 C250,105 220,75 190,75 C160,75 140,105 160,135 C180,165 220,180 250,165 C280,150 290,120 270,90' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.4'/%3E%3Cpath d='M220,120 C220,90 190,60 160,60 C130,60 110,90 130,120 C150,150 190,165 220,150' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.5'/%3E%3Ccircle cx='165' cy='105' r='4' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='235' cy='145' r='5' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='185' cy='165' r='3' fill='%232196f3' fill-opacity='0.2'/%3E%3C/svg%3E");
  transform: rotate(-45deg);
  animation: vineGrowLeft 3s ease-out forwards;
  opacity: 0;
}

.login-page::after {
  top: -50px;
  right: -50px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 300'%3E%3Cpath d='M280,150 C280,120 250,90 220,90 C190,90 170,120 190,150 C210,180 250,195 280,180 C310,165 320,135 300,105 C280,75 230,60 180,75 C130,90 90,135 110,180 C130,225 180,255 230,240 C280,225 310,180 290,135' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.3'/%3E%3Cpath d='M250,135 C250,105 220,75 190,75 C160,75 140,105 160,135 C180,165 220,180 250,165 C280,150 290,120 270,90' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.4'/%3E%3Cpath d='M220,120 C220,90 190,60 160,60 C130,60 110,90 130,120 C150,150 190,165 220,150' style='fill:none;stroke:%232196f3;stroke-width:1;stroke-opacity:0.5'/%3E%3Ccircle cx='165' cy='105' r='4' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='235' cy='145' r='5' fill='%232196f3' fill-opacity='0.2'/%3E%3Ccircle cx='185' cy='165' r='3' fill='%232196f3' fill-opacity='0.2'/%3E%3C/svg%3E");
  transform: rotate(45deg);
  animation: vineGrowRight 3s ease-out forwards;
  opacity: 0;
}

@keyframes vineGrowLeft {
  0% {
    opacity: 0;
    transform: scale(0.8) rotate(-45deg) translate(-50px, -50px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(-45deg) translate(0, 0);
  }
}

@keyframes vineGrowRight {
  0% {
    opacity: 0;
    transform: scale(0.8) rotate(45deg) translate(50px, -50px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(45deg) translate(0, 0);
  }
}

.login-page .content-wrapper {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  margin-top: -10vh;
  position: relative;
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.section {
  position: relative;
  border: 2px solid #e0e0e0;
  border-radius: 15px;
  padding: 25px 20px;
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.9);
}

.title {
  text-align: center;
  font-size: 28px;
  color: #333;
  margin-bottom: 30px;
  font-weight: 500;
  position: relative;
  font-family: 'Ma Shan Zheng', 'Cinzel', 'YouYuan', 'STXingkai', serif;
  text-shadow: 0 0 6px rgba(100, 181, 246, 0.4),
               0 0 12px rgba(33, 150, 243, 0.2);
  animation: titleGlow 2s ease-in-out infinite alternate;
  letter-spacing: 1px;
}

.title::before,
.title::after {
  content: '✦';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #2196f3;
  font-size: 16px;
  opacity: 0.8;
}

.title::before {
  left: -25px;
}

.title::after {
  right: -25px;
}

@keyframes titleGlow {
  from {
    text-shadow: 0 0 6px rgba(100, 181, 246, 0.4),
                 0 0 12px rgba(33, 150, 243, 0.2);
  }
  to {
    text-shadow: 0 0 8px rgba(100, 181, 246, 0.6),
                 0 0 16px rgba(33, 150, 243, 0.3);
  }
}

.invite-code-section {
  background: #f8f8f8;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  text-align: center;
}

.invite-code-section .label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.invite-code-section .code {
  font-size: 24px;
  color: #333;
  font-weight: bold;
  letter-spacing: 2px;
}

:deep(.van-field__button) {
  .van-button {
    height: 32px;
    padding: 0 16px;
  }
}
</style>
